<template>
  <main class="faq-container" role="main" aria-labelledby="faq-title">
    <!-- Header Section -->
    <section class="header-section" aria-labelledby="faq-title">
      <h1 class="faq-title" id="faq-title">
        Frequently Asked <br />
        <span class="subtitle-gradient">Questions</span>
      </h1>
      <p class="faq-description">
        Find answers to common questions about art-science collaborations, payment processes, and platform features.
      </p>
    </section>

    <!-- Admin Controls -->
    <div v-if="isAdmin" class="admin-controls">
      <button @click="addQuestion" class="add-question-btn">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 5v14m-7-7h14"/>
        </svg>
        New Question
      </button>
    </div>

    <!-- Search & Filter Section -->
    <section class="search-section" role="search" aria-labelledby="search-heading">
      <h2 class="visually-hidden" id="search-heading">Search and Filter FAQs</h2>
      <div class="search-container">
        <div class="filter-icon-section">
          <button class="filter-button" @click="toggleFilterDropdown" type="button"
                  aria-label="Filter options"
                  :aria-expanded="showFilterDropdown.toString()"
                  aria-haspopup="true">
            <svg viewBox="0 0 512 512" height="16" width="16" aria-hidden="true">
              <path d="M0 416c0 17.7 14.3 32 32 32l54.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 448c17.7 0 32-14.3 32-32s-14.3-32-32-32l-246.7 0c-12.3-28.3-40.5-48-73.3-48s-61 19.7-73.3 48L32 384c-17.7 0-32 14.3-32 32zm128 0a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zM320 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm32-80c-32.8 0-61 19.7-73.3 48L32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l246.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48l54.7 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-54.7 0c-12.3-28.3-40.5-48-73.3-48zM192 128a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm73.3-64C253 35.7 224.8 16 192 16s-61 19.7-73.3 48L32 64C14.3 64 0 78.3 0 96s14.3 32 32 32l86.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 128c17.7 0 32-14.3 32-32s-14.3-32-32-32L265.3 64z" fill="currentColor"/>
            </svg>
          </button>

          <div v-if="showFilterDropdown" class="faq-filter-dropdown">
            <div class="filter-section">
              <div class="filter-header">Filter by Audience</div>
              <div class="filter-option" :class="{ active: activeFilter === 'all' }" @click.stop="setFilter('all')">
                <span class="filter-check">{{ activeFilter === 'all' ? '✓' : '' }}</span>
                <span class="filter-label">All Questions</span>
              </div>
              <div class="filter-option" :class="{ active: activeFilter === 'artist' }" @click.stop="setFilter('artist')">
                <span class="filter-check artist-check">{{ activeFilter === 'artist' ? '✓' : '' }}</span>
                <span class="filter-label">For Artists</span>
              </div>
              <div class="filter-option" :class="{ active: activeFilter === 'scientist' }" @click.stop="setFilter('scientist')">
                <span class="filter-check scientist-check">{{ activeFilter === 'scientist' ? '✓' : '' }}</span>
                <span class="filter-label">For Scientists</span>
              </div>
            </div>
          </div>
        </div>
        <label for="faq-search" class="visually-hidden">Search frequently asked questions</label>
        <input
          id="faq-search"
          v-model="searchQuery"
          type="text"
          placeholder="Search FAQs..."
          class="search-input"
          aria-describedby="search-results-status"
          @input="announceSearchResults"
        />
        <button
          v-if="searchQuery"
          @click="clearSearch"
          @keydown.enter="clearSearch"
          class="clear-btn"
          aria-label="Clear search"
          tabindex="0"
        >✕</button>
      </div>
      <div id="search-results-status" class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{{ searchResultsAnnouncement }}</div>
    </section>

    <!-- FAQ Content -->
    <section class="faq-content" id="faq-content" role="tabpanel" aria-labelledby="search-heading">
      <div
        v-for="(faq, index) in filteredFaqs"
        :key="faq._uid || index"
        class="faq-item"
        :class="{
          open: faq.isOpen,
          'artist-item': faq.category === 'artist',
          'scientist-item': faq.category === 'scientist',
          'both-item': faq.category === 'both'
        }"
      >
        <!-- Edit Mode -->
        <div v-if="isAdmin && faq.editing" class="edit-container">
          <div class="edit-header">
            <h3>Editing Question</h3>
            <div class="edit-actions">
              <button @click="saveQuestion(faq)" class="save-btn">Save</button>
              <button @click="cancelEditQuestion(faq)" class="cancel-btn">Cancel</button>
            </div>
          </div>

          <div class="form-section">
            <div class="form-row">
              <div class="form-group flex-grow">
                <label :for="`question-${faq._uid}`">Question</label>
                <input
                  :id="`question-${faq._uid}`"
                  v-model="faq.question"
                  class="form-input"
                  placeholder="Enter the question..."
                />
              </div>
              <div class="form-group focus-field">
                <label :for="`focus-${faq._uid}`">Focus</label>
                <select :id="`focus-${faq._uid}`" v-model="faq.category" class="form-select">
                  <option value="artist">For Artists</option>
                  <option value="scientist">For Scientists</option>
                  <option value="both">For Both</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label :for="`answer-${faq._uid}`">Answer</label>
              <textarea
                :id="`answer-${faq._uid}`"
                v-model="faq.description"
                class="form-textarea"
                placeholder="Enter the answer..."
                rows="4"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Display Mode -->
        <div v-else>
          <div class="faq-header">
            <button
              @click="toggleFaq(faq)"
              @keydown.enter="toggleFaq(faq)"
              @keydown.space.prevent="toggleFaq(faq)"
              class="faq-question"
              :aria-expanded="faq.isOpen"
              :aria-controls="`faq-answer-${index}`"
              :id="`faq-question-${index}`"
              tabindex="0"
            >
              <div class="question-content">
                <h3>{{ faq.question }}</h3>
              </div>
              <i class="fas fa-chevron-down" :class="{ rotated: faq.isOpen }" aria-hidden="true"></i>
            </button>

            <div v-if="isAdmin" class="faq-actions">
              <button @click.stop="editQuestion(faq)" class="action-btn edit-action" :aria-label="`Edit question: ${faq.question}`">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button @click.stop="removeQuestion(faq)" class="action-btn delete-action" :aria-label="`Delete question: ${faq.question}`">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3,6 5,6 21,6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          </div>

          <div
            v-if="faq.isOpen"
            class="faq-answer"
            :id="`faq-answer-${index}`"
            role="region"
            :aria-labelledby="`faq-question-${index}`"
          >
            <div v-html="faq.description"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact-section" aria-labelledby="contact-heading">
      <h2 id="contact-heading">Still have questions?</h2>
      <button
        @click="openContactModal"
        @keydown.enter="openContactModal"
        class="contact-btn"
        aria-label="Contact support team for personalized assistance"
        tabindex="0"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
        <span>Contact Support</span>
      </button>
    </section>

    <ContactModal />
  </main>
</template>

<script>
import ContactModal from './ContactModal.vue';
import { openContactForm } from '../utils/eventBus';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, getDocs, doc } from 'firebase/firestore';

export default {
  name: 'BestPractices',
  components: {
    ContactModal
  },
  data() {
    return {
      searchQuery: '',
      activeFilter: 'all',
      showFilterDropdown: false,
      randomSeed: Math.random(), // For consistent random sorting until filter changes
      searchResultsAnnouncement: '',
      isLoading: false,
      faqs: [
        {
          category: 'artist',
          question: 'What are the benefits to using PALLAS?',
          description: 'The main function of PALLAS is to facilitate connections between scientists and artists interested in science communication. We offer connections to a network of 100+ scientists across astrobiology, earth science, and the physical sciences.',
          isOpen: false,
        },
        {
          category: 'artist',
          question: 'What is PALLAS doing to protect artists from AI?',
          description: 'We provide each artist with a page to display selected artwork. Before uploading artwork, we suggest artists run their artwork through Glaze, a software developed by UChicago (<a href="https://glaze.cs.uchicago.edu/" target="_blank">https://glaze.cs.uchicago.edu/</a>) to disrupt AI art generators\' ability to mimic your art. (Please note that Glaze is not a total solution to this problem, since it is not equally effective for all art styles). We also invite artists to watermark their work in any way they see fit.',
          isOpen: false,
        },
        {
          category: 'artist',
          question: "I don't have a lot of experience in science, how can I translate the work accurately into visual form?",
          description: 'If you are an artist transitioning to scientific illustration or a science illustrator exploring a new field, you may be unfamiliar with the technical aspects a scientist may want to communicate. We recommend scientists bring a layman description of the project along with a sketch of their idea. Artists can then take the lead to propose iterations that work with the time and budget. We also encourage that scientists integrate artists throughout the process and especially from the start of the process if possible (e.g., consult an artist from the beginning stages of idea conception and manuscript/proposal writing).',
          isOpen: false,
        },
        {
          category: 'artist',
          question: 'How do I make sure I get paid?',
          description: 'As a disclaimer, PALLAS is not involved with the transaction process between scientists and artists and as such is not responsible for requiring, moderating, or enforcing payment; hosting any payment services; providing discounts; settling disputes, etc. Payment is entirely as agreed upon between the scientist and artist.<br><br>Artists - we recommend: a) specifying all rates, payment, and deadlines clearly in the first message to a scientist/client in order to have a written agreement on deliverables and payment; and b) asking for some percentage of payment upfront (often 25-50%). We especially encourage you to seek payment upfront if the client\'s account is new or missing some information. Always follow your gut for your own safety.',
          isOpen: false,
        },
        {
          category: 'artist',
          question: 'How is PALLAS screening artists/moderating scientists?',
          description: 'We review every application individually to check and ensure that each account is created by the individual intended. This also allows us to ensure that the profile is created by an actual scientist or artist, and not by spam or bot accounts. We filter each profile by account type (scientist, artist, etc.) to streamline the search process and avoid irrelevant searches.',
          isOpen: false,
        },
        {
          category: 'artist',
          question: 'What are the restrictions for PALLAS artists around using stock libraries, AI?',
          description: 'PALLAS places no restrictions on our artists\' medium or methods besides a good faith assumption that there is no use of generative AI (ChatGPT, DALL-E) with the intent to take credit for the generated image. However, stock image and asset libraries may place their own restrictions on usage, and publishing venues may place additional restrictions on artwork that we recommend referring to as you collaborate.',
          isOpen: false,
        },
        {
          category: 'artist',
          question: 'How do I negotiate and set rates for my scientific illustration work?',
          description: 'Setting rates can be challenging for artists new to scientific illustration. Consider factors like:<br>• Project complexity and technical requirements<br>• Time investment (research, revisions, meetings)<br>• Usage rights (publication, presentations, web)<br>• Your experience level and portfolio<br>• Market rates in your region<br><br>We recommend researching standard rates in scientific illustration, joining professional organizations for guidance, and being transparent about your pricing structure upfront.',
          isOpen: false,
        },
        {
          category: 'artist',
          question: 'What should I include in my PALLAS portfolio?',
          description: 'Your portfolio should showcase:<br>• Diverse examples of your work (different styles, subjects)<br>• Any science-related illustrations you\'ve created<br>• Technical accuracy demonstrations<br>• Process work (sketches to final pieces)<br>• Clear descriptions of each piece<br>• Your artistic range and specialties<br><br>Quality over quantity - 5-10 strong pieces are better than 20 mediocre ones.',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: 'Why hire an artist?',
          description: 'Science communication encompasses a variety of media and display items, including but not limited to visual communication (renderings, schematics, technical diagrams, concept illustrations, infographics, and improvements on graphical figure design). This media may be tailored to a range of audiences, again including but not limited to outreach, teaching, conference presentations, public media coverage, or funding proposals. Understanding different media and audiences takes skill that scientists may not have extensive training in, or have the time to invest in for every paper or presentation - just like collaborating with other scientists with different expertise.',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: 'Why should I hire an artist instead of using Generative AI?',
          description: 'Many high impact journals like Nature and Science, as well as several publishers like Elsevier, have policies banning the use of AI-generated images without clear labeling, on grounds of copyright infringement, accuracy concerns, and reproducibility, while others strongly caution against such usage. Using AI-generated images, especially without proper disclosure, can compromise scientific integrity and may lead to publication rejection or retraction.<br><br>Artists bring human creativity, scientific understanding, and the ability to iterate based on your specific needs. They ensure accuracy, originality, and compliance with publication standards while creating custom visuals that truly represent your research.',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: 'How do I fund an artist?',
          description: 'Many grants now recognize the importance of science communication and allow budgeting for:<br>• Broader Impacts activities<br>• Outreach and education materials<br>• Publication and dissemination costs<br>• Science communication personnel<br><br>Check your grant guidelines or speak with your program officer about including artist collaboration costs. Some institutions also have internal funding for science communication initiatives.',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: 'How do I find an artist on PALLAS?',
          description: 'Use our search and filter features to find artists by:<br>• Art medium (digital, traditional, 3D, etc.)<br>• Subject expertise<br>• Style preferences<br>• Availability for commissions<br><br>Review portfolios, check artist statements, and reach out directly through the platform. You can also post project opportunities in our community forums.',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: "I'm not good at sketching—how can I communicate my ideas?",
          description: 'You don\'t need to be an artist! Here are effective ways to communicate your vision:<br>• Create simple diagrams or flowcharts<br>• Use reference images from papers or online<br>• Write detailed descriptions of what you envision<br>• Share the key scientific concepts to convey<br>• Provide examples of styles you like<br><br>Artists are skilled at interpreting scientific concepts and will work with you to develop the visuals.',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: 'Does PALLAS provide contracts or handle payments?',
          description: 'PALLAS facilitates connections but does not provide contracts or handle payments. We recommend:<br>• Discussing all terms in writing (email is fine)<br>• Clarifying deliverables, timeline, and payment<br>• Agreeing on revision rounds<br>• Defining usage rights<br>• Using your institution\'s procurement process if applicable<br><br>Many universities have standard contracts for creative services that you can adapt.',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: 'What information should I provide to an artist?',
          description: 'A good project brief includes:<br>• Project overview and scientific context<br>• Target audience (peers, public, students)<br>• Intended use (publication, presentation, web)<br>• Technical requirements (size, format, resolution)<br>• Key elements that must be included<br>• Timeline and budget<br>• Examples or style preferences<br>• Number of revision rounds expected',
          isOpen: false,
        },
        {
          category: 'scientist',
          question: 'How far in advance should I contact an artist?',
          description: 'Plan ahead! Quality scientific illustration takes time:<br>• Simple diagrams: 1-2 weeks<br>• Complex illustrations: 3-6 weeks<br>• Large projects: 2-3 months<br><br>Factor in time for:<br>• Initial discussions and quotes<br>• Artist research and concept development<br>• Revision rounds<br>• Final formatting<br>• Institutional approval processes<br><br>Contact artists as early as possible, ideally when writing grants or planning publications.',
          isOpen: false,
        },
      ]
    };
  },
  computed: {
    isAdmin() {
      try {
        const userInfo = localStorage.getItem('user-info');
        if (userInfo) {
          const parsed = JSON.parse(userInfo);
          return parsed.adminRole === 'admin' || parsed.role === 'admin';
        }
        return false;
      } catch {
        return false;
      }
    },
    filteredFaqs() {
      // Separate editing FAQs to keep them at top
      const editingFaqs = this.faqs.filter(faq => faq.editing);
      let nonEditingFaqs = this.faqs.filter(faq => !faq.editing);

      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        nonEditingFaqs = nonEditingFaqs.filter(faq =>
          faq.question.toLowerCase().includes(query) ||
          faq.description.toLowerCase().includes(query)
        );
      }

      // Filter by category
      if (this.activeFilter !== 'all') {
        nonEditingFaqs = nonEditingFaqs.filter(faq =>
          faq.category === this.activeFilter || faq.category === 'both'
        );
      } else if (!this.searchQuery.trim()) {
        // Use seeded random shuffle for consistent ordering
        nonEditingFaqs = this.shuffleArray(nonEditingFaqs, this.randomSeed);
      }

      // Always keep editing FAQs at the top
      return [...editingFaqs, ...nonEditingFaqs];
    }
  },
  methods: {
    toggleFaq(faq) {
      faq.isOpen = !faq.isOpen;

      // Announce state change to screen readers
      this.$nextTick(() => {
        const announcement = faq.isOpen ?
          `Expanded: ${faq.question}` :
          `Collapsed: ${faq.question}`;
        this.announceToScreenReader(announcement);
      });
    },
    toggleFilterDropdown() {
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    setFilter(filter) {
      this.activeFilter = filter;
      this.showFilterDropdown = false;
      // Generate new random seed when filter changes for fresh random order
      if (filter === 'all') {
        this.randomSeed = Math.random();
      }

      // Announce filter change
      this.$nextTick(() => {
        const filterLabel = filter === 'all' ? 'All Questions' :
                           filter === 'artist' ? 'For Artists' : 'For Scientists';
        this.announceToScreenReader(`Filtered to: ${filterLabel}. Showing ${this.filteredFaqs.length} questions.`);
      });
    },
    clearSearch() {
      this.searchQuery = '';
      this.announceSearchResults();
    },
    // Admin methods
    addQuestion() {
      const newFaq = {
        _uid: `faq-${Date.now()}`,
        category: 'both',
        question: 'New Question',
        description: 'Enter the answer here...',
        isOpen: false,
        editing: true,
        isNew: true
      };
      this.faqs.unshift(newFaq);
    },
    editQuestion(faq) {
      // Store original values for cancel
      faq._originalQuestion = faq.question;
      faq._originalDescription = faq.description;
      faq._originalCategory = faq.category;
      faq.editing = true;
    },
    async saveQuestion(faq) {
      try {
        if (faq.isNew) {
          // Add new FAQ to Firestore
          const docRef = await addDoc(collection(db, 'faqs'), {
            category: faq.category,
            question: faq.question,
            description: faq.description,
            createdAt: new Date().toISOString()
          });
          faq._uid = docRef.id;
          delete faq.isNew;
        } else if (faq._uid && !faq._uid.startsWith('faq-')) {
          // Update existing FAQ in Firestore
          await updateDoc(doc(db, 'faqs', faq._uid), {
            category: faq.category,
            question: faq.question,
            description: faq.description,
            updatedAt: new Date().toISOString()
          });
        }
        // Clear editing state
        faq.editing = false;
        delete faq._originalQuestion;
        delete faq._originalDescription;
        delete faq._originalCategory;
      } catch (error) {
        console.error('Error saving FAQ:', error);
      }
    },
    cancelEditQuestion(faq) {
      if (faq.isNew) {
        // Remove new unsaved FAQ
        const index = this.faqs.indexOf(faq);
        if (index > -1) {
          this.faqs.splice(index, 1);
        }
      } else {
        // Restore original values
        faq.question = faq._originalQuestion;
        faq.description = faq._originalDescription;
        faq.category = faq._originalCategory;
        faq.editing = false;
        delete faq._originalQuestion;
        delete faq._originalDescription;
        delete faq._originalCategory;
      }
    },
    async removeQuestion(faq) {
      if (!confirm('Are you sure you want to delete this question?')) {
        return;
      }
      try {
        if (faq._uid && !faq._uid.startsWith('faq-')) {
          // Delete from Firestore
          await deleteDoc(doc(db, 'faqs', faq._uid));
        }
        // Remove from local array
        const index = this.faqs.indexOf(faq);
        if (index > -1) {
          this.faqs.splice(index, 1);
        }
      } catch (error) {
        console.error('Error deleting FAQ:', error);
      }
    },
    async loadFaqs() {
      try {
        this.isLoading = true;
        const querySnapshot = await getDocs(collection(db, 'faqs'));
        if (!querySnapshot.empty) {
          const firestoreFaqs = querySnapshot.docs.map(doc => ({
            _uid: doc.id,
            ...doc.data(),
            isOpen: false,
            editing: false
          }));
          // Merge with default FAQs, prioritizing Firestore data
          this.faqs = [...firestoreFaqs, ...this.faqs.filter(f => !f._uid || f._uid.startsWith('faq-'))];
        }
      } catch (error) {
        console.error('Error loading FAQs:', error);
      } finally {
        this.isLoading = false;
      }
    },
    openContactModal() {
      openContactForm();
    },
    announceSearchResults() {
      this.$nextTick(() => {
        const count = this.filteredFaqs.length;
        if (this.searchQuery.trim()) {
          this.searchResultsAnnouncement = count === 1 ?
            `Found ${count} question matching "${this.searchQuery}"` :
            `Found ${count} questions matching "${this.searchQuery}"`;
        } else {
          this.searchResultsAnnouncement = `Showing all questions. ${count} total.`;
        }
      });
    },
    announceToScreenReader(message) {
      // Create a temporary live region for announcements
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.classList.add('visually-hidden');
      announcement.textContent = message;

      document.body.appendChild(announcement);

      // Remove after announcement
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    },
    shuffleArray(array, seed) {
      // Create a seeded random function for consistent shuffling
      const seededRandom = this.seededRandom(seed);
      const shuffled = [...array];
      
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(seededRandom() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      
      return shuffled;
    },
    seededRandom(seed) {
      let x = Math.sin(seed) * 10000;
      return function() {
        x = Math.sin(x) * 10000;
        return x - Math.floor(x);
      };
    }
  },
  mounted() {
    this.loadFaqs();
  }
};
</script>

<style scoped>
.faq-container {
  width: 100%;
  max-width: var(--maxContentWidth);
  margin: 0 auto;
  padding: var(--paddingSpaceous);
  margin-top: var(--navbarHeight);
  min-height: calc(100vh - var(--navbarHeight));
}

/* Header Section */
.header-section {
  text-align: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  margin-bottom: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.faq-title {
  font-size: var(--mainTitle);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  line-height: 0.9;
  margin: 0 0 var(--paddingStandard) 0;
}

.subtitle-gradient {
  font-style: italic;
  font-size: var(--subTitle);
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.faq-description {
  font-size: var(--titleSubText);
  color: var(--textColor);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

/* Search & Filter Section */
.search-section {
  margin-bottom: var(--paddingSpaceous);
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
  align-items: center;
}

.search-container {
  position: relative;
  width: 100%;
  max-width: 500px;
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid var(--borderColor);
  border-radius: var(--stdRadius);
  overflow: visible;
}

.filter-icon-section {
  position: relative;
  display: flex;
  align-items: center;
}

.filter-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: transparent;
  border: none;
  border-right: 1px solid var(--borderColor);
  cursor: pointer;
  color: var(--textColor);
  transition: background 0.2s ease, color 0.2s ease;
}

.filter-button:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--headersColor);
}

.faq-filter-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 180px;
}

.filter-section {
  padding: var(--paddingTight);
}

.filter-header {
  font-size: var(--labelText);
  font-weight: 600;
  color: var(--headersColor);
  padding: var(--paddingTight);
  border-bottom: 1px solid var(--borderColor);
  margin-bottom: var(--paddingTight);
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px var(--paddingTight);
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.filter-option:hover {
  background: rgba(0, 0, 0, 0.05);
}

.filter-option.active {
  background: rgba(209, 210, 230, 0.3);
}

.filter-check {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: var(--headersColor);
}

.filter-check.artist-check {
  color: var(--artColor);
}

.filter-check.scientist-check {
  color: var(--sciColor);
}

.filter-label {
  font-size: var(--labelText);
  color: var(--textColor);
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  border: none;
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  background: transparent;
  color: var(--textColor);
  outline: none;
}

.search-input:focus {
  outline: none;
}


.clear-btn {
  padding: 12px;
  background: none;
  border: none;
  color: var(--textColor);
  cursor: pointer;
  font-size: 16px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.clear-btn:hover {
  opacity: 1;
}

/* Admin Controls */
.admin-controls {
  display: flex;
  justify-content: center;
  margin-bottom: var(--paddingSpaceous);
}

.add-question-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 20px;
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  font-weight: 600;
  background: white;
  color: var(--headersColor);
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  border: 2px solid #d1d2e6;
}

.add-question-btn svg {
  stroke: var(--headersColor);
}

.add-question-btn:hover {
  background: #d1d2e6;
}

/* FAQ Content */
.faq-content {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
  margin-bottom: var(--paddingSpaceous);
}

.faq-item {
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

/* FAQ Header with Actions */
.faq-header {
  display: flex;
  align-items: flex-start;
}

.faq-header .faq-question {
  flex: 1;
}

.faq-actions {
  display: flex;
  gap: 0.5rem;
  padding: var(--paddingStandard);
  padding-left: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  background: white;
}

.edit-action {
  border: 2px solid #d1d2e6;
  color: var(--headersColor);
}

.edit-action:hover {
  background: #d1d2e6;
}

.delete-action {
  border: 2px solid #ccc;
  color: #666;
}

.delete-action:hover {
  background: #ccc;
  color: #333;
}

/* Edit Container */
.edit-container {
  padding: var(--paddingStandard);
  background: white;
}

.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--paddingStandard);
  padding-bottom: var(--paddingTight);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.edit-header h3 {
  margin: 0;
  color: var(--headersColor);
  font-size: var(--subTitle);
  font-weight: var(--font-weight-bold);
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
}

.save-btn, .cancel-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-family: var(--font-primary);
  font-weight: 600;
  border-radius: var(--stdRadius);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  background: white;
}

.save-btn {
  border: 2px solid #d1d2e6;
  color: var(--headersColor);
}

.cancel-btn {
  border: 2px solid #ccc;
  color: #666;
}

.save-btn:hover {
  background: #d1d2e6;
}

.cancel-btn:hover {
  background: #ccc;
  color: #333;
}

/* Form Styles */
.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
}

.form-row {
  display: flex;
  gap: var(--paddingStandard);
  align-items: flex-start;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--paddingTight);
}

.form-group.flex-grow {
  flex: 1;
}

.form-group.focus-field {
  width: 150px;
  flex-shrink: 0;
}

.form-group label {
  font-weight: 600;
  color: var(--headersColor);
  font-size: var(--labelText);
}

.form-input, .form-textarea, .form-select {
  width: 100%;
  padding: var(--paddingTight) var(--paddingStandard);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  font-size: var(--labelText);
  color: var(--textColor);
  background: white;
  font-family: var(--font-primary);
  transition: border-color 0.2s ease;
}

.form-input:focus, .form-textarea:focus, .form-select:focus {
  outline: none;
  border-color: var(--sciColor);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.form-select {
  cursor: pointer;
}


.faq-question {
  width: 100%;
  padding: var(--paddingSpaceous);
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  transition: background-color 0.3s ease;
}

.faq-question:hover {
  background: white;
}

.question-content {
  flex: 1;
  margin-right: var(--paddingStandard);
}

.question-content h3 {
  margin: 0;
  font-size: var(--buttonText);
  font-weight: var(--font-weight-bold);
  color: var(--headersColor);
  line-height: 1.4;
}

.fa-chevron-down {
  color: var(--textColor);
  opacity: 0.6;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.fa-chevron-down.rotated {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 var(--paddingSpaceous) var(--paddingSpaceous);
  color: var(--textColor);
  font-size: var(--buttonText);
  line-height: 1.6;
  border-top: 1px solid var(--borderColor);
  background: #ffffff;
  animation: slideDown 0.3s ease-out;
}

.faq-answer a {
  color: var(--sciColor);
  text-decoration: underline;
}

.faq-answer a:hover {
  color: var(--artColor);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Contact Section */
.contact-section {
  text-align: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: var(--stdRadius);
  padding: var(--paddingSpaceous);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.contact-section h2 {
  font-size: var(--subTitle);
  color: var(--textColor);
  margin-bottom: var(--paddingStandard);
  font-weight: var(--font-weight-bold);
}

.contact-section p {
  font-size: var(--buttonText);
  color: var(--textColor);
  line-height: 1.6;
  margin-bottom: var(--paddingSpaceous);
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  background: var(--bckColor);
  border: none;
  border-radius: var(--stdRadius);
  font-size: var(--buttonText);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: visible;
}

.contact-btn::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
}

.contact-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  z-index: 0;
}

.contact-btn span,
.contact-btn svg {
  position: relative;
  z-index: 2;
  color: var(--textColor);
}

.contact-btn svg path {
  stroke: var(--textColor);
}

.contact-btn:hover,
.contact-btn:focus {
  transform: translateY(-2px);
  outline: none;
}

/* Accessibility improvements */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Focus styles for all interactive elements */
.search-input:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

.clear-btn:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
  opacity: 1;
}

.filter-button:focus {
  outline: none;
}

.faq-question:focus {
  outline: none;
  background: white;
}

/* Responsive Design */
@media (max-width: 768px) {
  .faq-question {
    padding: var(--paddingStandard);
  }

  .faq-answer {
    padding: 0 var(--paddingStandard) var(--paddingStandard);
  }

  .search-container {
    max-width: 100%;
  }

  .form-row {
    flex-direction: column;
  }

  .form-group.focus-field {
    width: 100%;
  }

  .edit-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--paddingStandard);
  }

  .faq-header {
    flex-direction: column;
  }

  .faq-actions {
    padding: 0 var(--paddingStandard) var(--paddingStandard);
  }
}

@media (max-width: 480px) {
  .faq-title {
    font-size: calc(var(--mainTitle) * 0.8);
  }

  .faq-description {
    font-size: var(--buttonText);
  }

  .search-input {
    font-size: var(--labelText);
    padding: 10px 12px;
  }

  .filter-button {
    padding: 10px;
  }
}
</style>